import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import type { Hotel } from 'frontend-utils/models';
import { type RoomType, ROOMTYPES } from 'frontend-utils/models/roomtype';
import {
  type Platform,
  PLATFORMS,
  TYPE_PARITY,
  TYPE_PARITY_INSIGHT,
} from 'frontend-utils/utils/constants/constant-values';
import fnMedian from 'frontend-utils/utils/numeric/find-median';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type HotelParityListRoute from 'lumen/routes/hotel/parity/list';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  LIVEUPDATE_TYPE_PARITY,
  LIVEUPDATE_TYPE_PARITY_INSIGHT,
} from 'lumen/services/liveupdate';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  MEALTYPES,
  type MealTypeValues,
  type MembershipType,
  MEMBERSHIPTYPES,
  OTA_GROUPS,
  RATE_PARAMS,
  type RateParam,
} from 'lumen/utils/constants/general-constants';
import type {
  CountryCodeWithDefault,
  OTAGroup,
} from 'lumen/utils/constants/pi-constant-values';
import { DEFAULT_POS } from 'lumen/utils/constants/pi-constant-values';
import { logReportInput } from 'lumen/utils/log-report-input';
import { formatUrl } from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';

interface ExportRequestParams {
  base_membershiptype: MembershipType;
  from_date_range_end?: string;
  from_date_range_start?: string;
  hotel_id: string;
  los: number;
  mealtype: MealTypeValues;
  membershiptype: MembershipType;
  persons?: number;
  platform: Platform;
  rate_param: RateParam;
  roomtype: RoomType;
  type?: string;
}

export type Selections =
  | { key: 'rateParam'; value: RateParam }
  | { key: 'los'; value: number }
  | { key: 'pos'; value: CountryCodeWithDefault }
  | { key: 'maxPersons'; value: number }
  | { key: 'membershipType'; value: MembershipType }
  | { key: 'roomType'; value: RoomType }
  | { key: 'mealPlan'; value: MealTypeValues }
  | { key: 'platform'; value: Platform }
  | { key: 'month'; value: string | null }
  | { key: 'parityAnalyticsView'; value: boolean }
  | { key: 'OTAGroup'; value: OTAGroup };
export type SelectionsKey = Selections['key'];
export type SelectionsValue = Selections['value'];

export default class HotelParityListController extends Controller {
  declare model: ModelFrom<HotelParityListRoute>;
  // @ts-expect-error: Property 'queryParams' in type 'ListController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    { rateParam: { scope: 'controller' } },
    { los: { scope: 'controller' } },
    { pos: { scope: 'controller' } },
    { maxPersons: { scope: 'controller' } },
    { membershipType: { scope: 'controller' } },
    { roomType: { scope: 'controller' } },
    { mealPlan: { scope: 'controller' } },
    { month: { scope: 'controller' } },
    { scheduleReport: { scope: 'controller' } },
    { parityAnalyticsView: { scope: 'controller' } },
    { OTAGroup: { scope: 'controller' } },
    { platform: { scope: 'controller' } },
    { showMemberRate: { scope: 'controller' } },
  ] as const;

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked los: number = DEFAULT_QP_VALUES.hotel.parity.list.los;
  @tracked maxPersons: number = DEFAULT_QP_VALUES.hotel.parity.list.maxPersons;
  @tracked mealPlan: MealTypeValues =
    DEFAULT_QP_VALUES.hotel.parity.list.mealPlan;
  @tracked membershipType: MembershipType =
    DEFAULT_QP_VALUES.hotel.parity.list.membershipType;
  @tracked month: string | null = null;
  @tracked OTAGroup: OTAGroup = DEFAULT_QP_VALUES.hotel.parity.list.OTAGroup;
  @tracked OTAId = DEFAULT_QP_VALUES.hotel.parity.list.OTAId;
  @tracked parityAnalyticsView: boolean = false;
  @tracked platform: Platform = DEFAULT_QP_VALUES.hotel.parity.list.platform;
  @tracked pos: CountryCodeWithDefault =
    DEFAULT_QP_VALUES.hotel.parity.list.pos;
  @tracked rateParam: RateParam = DEFAULT_QP_VALUES.hotel.parity.list.rateParam;
  @tracked roomType: RoomType = DEFAULT_QP_VALUES.hotel.parity.list.roomType;
  // is used as URL parameter
  @tracked scheduleReport = false;
  @tracked showMemberRate = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeLiveUpdate() {
    return this.liveupdate?.activeLiveupdatesForThisSelection?.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableOTAs() {
    if (this.isOtaHotelInfosRequestLoading) {
      return [];
    }

    // Exception for PA-only hotels and limited parity, they should make PA parities request
    const showParityAnalyticsOTAs =
      this.isLimitedParityView || this.showParityAnalyticsData;

    const otas = showParityAnalyticsOTAs
      ? [...this.hotel.getParityAnalyticsOTAs(this.platform)]
      : [...this.hotel.getParityOTAs(this.platform)];

    if (this.pos === DEFAULT_POS) {
      return otas.sort((a, b) => a.order - b.order);
    }

    const otaHotelInfos = this.model.otaHotelInfos.last!.value;
    const filteredOtas = otas.filter((ota) => {
      // Brand.com doesn't support POS shopping but should always be included
      if (ota.isBranddotcom) {
        return true;
      }
      // Check if POS shopping is supported for each OTA
      return otaHotelInfos?.some(
        (info) => info.otaId === ota.id && info.supportsPosShopping,
      );
    });

    return filteredOtas.sort((a, b) => a.order - b.order);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRange() {
    return {
      start: this.monthMoment.startOf('month'),
      end: this.monthMoment.endOf('month'),
    };
  }

  // Check feature flag for day live shops
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayLiveupdateEnabled() {
    return this.access.hasParityDayLiveupdate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exportChoices() {
    const disableEverythingExceptCustomAndSchedule =
      this.noDataForSelection?.exportDisabled;
    const shopDays = this.hotel.getParityExportShopDays(this.type, this.los);

    if (!this.showMeta) {
      return [
        {
          groupName: this.intl.t('general.export_group.export'),
          options: [
            {
              id: 'defined_report',
              monthly: false,
              label: `${this.intl.t('general.next_days', { count: shopDays })} Major OTAs`,
              type: 'major',
              selection: shopDays,
              disabled: disableEverythingExceptCustomAndSchedule,
            },
            {
              id: 'defined_report',
              monthly: true,
              label: `${this.intl.t('rates.active_month')} Major OTAs`,
              type: 'major',
              disabled: disableEverythingExceptCustomAndSchedule,
            },
          ],
        },
        {
          groupName: this.intl.t('reports.schedule_report'),
          options: [
            {
              id: 'schedule_report',
              label: this.intl.t('general.export_options.reports'),
              icon: 'calendar-clock',
            },
          ],
        },
      ];
    }

    return [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'defined_report',
            monthly: false,
            label: `${this.intl.t('general.next_days', { count: shopDays })} Major OTAs`,
            type: 'major',
            selection: shopDays,
            disabled: disableEverythingExceptCustomAndSchedule,
          },
          {
            id: 'defined_report',
            monthly: false,
            label: `${this.intl.t('general.next_days', { count: shopDays })} Other OTAs`,
            type: 'other',
            selection: shopDays,
            disabled:
              disableEverythingExceptCustomAndSchedule ||
              this.platform === PLATFORMS.PLATFORM_MOBILE_APP,
            message:
              this.platform === PLATFORMS.PLATFORM_MOBILE_APP
                ? tKey('reports.no_other_ota_report_available_platform_app')
                : undefined,
          },
          {
            id: 'defined_report',
            monthly: true,
            label: `${this.intl.t('rates.active_month')} Major OTAs`,
            type: 'major',
            disabled: disableEverythingExceptCustomAndSchedule,
          },
          {
            id: 'defined_report',
            monthly: true,
            label: `${this.intl.t('rates.active_month')} Other OTAs`,
            type: 'other',
            disabled:
              disableEverythingExceptCustomAndSchedule ||
              this.platform === PLATFORMS.PLATFORM_MOBILE_APP,
            message:
              this.platform === PLATFORMS.PLATFORM_MOBILE_APP
                ? tKey('reports.no_other_ota_report_available_platform_app')
                : undefined,
          },
        ],
      },
      {
        groupName: this.intl.t('reports.schedule_report'),
        options: [
          {
            id: 'schedule_report',
            label: this.intl.t('general.export_options.reports'),
            icon: 'calendar-clock',
          },
        ],
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasDefaultSelections() {
    const hasDefaultRateParam = this.rateParam === RATE_PARAMS.LOWEST;
    const hasDefaultMealPlan = this.mealPlan === MEALTYPES.ANY;
    const hasDefaultRoomType = this.roomType === ROOMTYPES.ALL;

    return hasDefaultRateParam && hasDefaultMealPlan && hasDefaultRoomType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasUpdateTimestamp() {
    return this.updateTimestamp;
  }

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLimitedParityView() {
    return this.access.hasLimitedParityView;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return (
      this.isPeriodsRequestLoading ||
      this.isRoomtypeRequestLoading ||
      this.isOtaHotelInfosRequestLoading
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMonthLiveupdateLimitReached() {
    return this.liveupdate?.isMonthLiveupdateLimitReached;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOtaHotelInfosRequestLoading() {
    return !this.model.otaHotelInfos.last || this.model.otaHotelInfos.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPeriodsRequestLoading() {
    return this.model.periods.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRoomtypeRequestLoading() {
    return this.model.roomtypes.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isScheduleDisabled() {
    if (this.rateParam === RATE_PARAMS.THIRD_PARTY) {
      return {
        disabled: true,
        message: this.intl.t('reports.no_report_for_third_party'),
      };
    }

    let message = this.hotelAndMarketService.getReportSchedulingMessage(
      this.type,
      this.los,
      null,
      this.membershipType,
      this.maxPersons,
      this.platform,
    );

    if (!this.hasDefaultSelections) {
      message = this.intl.t('reports.no_report_available');
    }

    const disabled = isPresent(message);
    return {
      disabled,
      message,
    };
  }

  // Check feature flag for month & custom live shops
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get liveupdateEnabled() {
    return this.access.hasParityCustomLiveupdate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDateMoment() {
    return this.hotelAndMarketService.getLocalTime().startOf('month');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthMoment() {
    return this.hotelAndMarketService.toLocalTime(`${this.month}-15`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noDataForSelection() {
    if (!this.isPeriodsRequestLoading) {
      const hasParities = this.periods
        ?.map((period) => {
          let result = 0;

          (period?.parities ?? []).forEach((parity) => {
            if (parity.message !== 'general.missing') {
              result = 1;
            }
          });
          return result;
        })
        .reduce((a, b) => a + b);
      if (hasParities === 0) {
        return {
          showOverlay:
            !this.activeLiveUpdate && this.access.hasParityCustomLiveupdate,
          hasMessage: !this.activeLiveUpdate,
          message: this.intl.t('rates.nodata.refresh'),
          exportDisabled: true,
        };
      }
    }

    return {
      showOverlay: false,
      hasMessage: false,
      message: '',
      exportDisabled: false,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parityType() {
    if (this.showParityAnalyticsData) {
      return LIVEUPDATE_TYPE_PARITY_INSIGHT;
    }

    return LIVEUPDATE_TYPE_PARITY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get periods() {
    if (this.isPeriodsRequestLoading) {
      return [];
    }
    return this.model.periods.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMeta() {
    const isLimitedParityWithOtaGroupSpecified =
      this.isLimitedParityView && this.OTAGroup !== OTA_GROUPS.ALL;
    const isMobileAppPlatform = this.platform === PLATFORMS.PLATFORM_MOBILE_APP;

    return (
      this.hasDefaultSelections &&
      !isLimitedParityWithOtaGroupSpecified &&
      !isMobileAppPlatform
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showParityAnalyticsData() {
    return this.access.hasParityAnalyticsStaff && this.parityAnalyticsView;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showUpdateTime() {
    return !dayjs
      .utc(this.month, 'YYYY-MM')
      .endOf('month')
      .isBefore(dayjs.utc());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get timestamp() {
    if (this.updateTimestamp !== null) {
      let updateTimestamp = this.updateTimestamp.fromNow();
      if (
        this.isLimitedParityView &&
        dayjs().diff(this.updateTimestamp, 'hour') > 10 &&
        !this.isMonthLiveupdateLimitReached
      ) {
        updateTimestamp += `. ${this.intl.t('parity.limited.perform_liveupdate').toString()}`;
      }
      return updateTimestamp;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get type() {
    if (this.isLimitedParityView || this.parityAnalyticsView) {
      return TYPE_PARITY_INSIGHT;
    }

    return TYPE_PARITY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get updateTimestamp() {
    const minPerDay: number[] = [];
    let updateTimestamp = null;

    this.periods?.forEach((period) => {
      const updateTimesUnix: number[] = [];
      if (period.parities) {
        period.parities.forEach((parity) => {
          if (parity.extract_datetime) {
            const extractDateTime = dayjs.utc(parity.extract_datetime).unix();
            if (extractDateTime > 0) {
              updateTimesUnix.push(extractDateTime);
            }
          }
        });
      }
      if (updateTimesUnix.length > 0) {
        const median = fnMedian(updateTimesUnix);
        if (median) {
          minPerDay.push(median);
        }
      }
    });

    if (minPerDay.length) {
      const median = Math.max.apply(null, minPerDay);
      updateTimestamp = dayjs.unix(median).utc();
    }
    return updateTimestamp;
  }

  @action closeScheduleModal(): void {
    this.scheduleReport = false;
  }

  @action handleExport(exportChoice: {
    id: string;
    monthly?: boolean;
    selection?: number;
    type?: string;
  }): void {
    if (exportChoice.id === 'schedule_report') {
      if (!this.isScheduleDisabled?.disabled) {
        this.scheduleReport = true;
      }
    } else if (exportChoice.monthly) {
      if (this.periods && this.periods?.length > 0) {
        const params: ExportRequestParams = {
          hotel_id: this.hotel.id!,
          rate_param: this.rateParam,
          los: this.los,
          roomtype: this.roomType,
          membershiptype: this.membershipType,
          mealtype: this.mealPlan,
          platform: this.platform,
          type: exportChoice.type,
          from_date_range_start: this.periods[0]?.fromDate.format('YYYY-MM-DD'),
          from_date_range_end:
            this.periods[this.periods.length - 1]?.fromDate.format(
              'YYYY-MM-DD',
            ),
          persons: this.maxPersons,
          base_membershiptype: this.showMemberRate
            ? MEMBERSHIPTYPES.MEMBER_ONLY
            : MEMBERSHIPTYPES.PUBLIC,
        };

        const url = formatUrl(
          '/apigateway/v1/app/download/integrities',
          params,
          this.ajax.host,
        );

        this.usageAnalytics.track('Data Exported', {
          eventTrigger: 'parity-table-view',
        });

        logReportInput(url, this.access.isStaffUser);

        openUrl(url);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          title: this.intl.t('rates.roomtype.error'),
          text: this.intl.t('parity.export.nodata'),
        });
      }
    } else {
      const today = this.hotelAndMarketService.getLocalTime();
      const params: ExportRequestParams = {
        hotel_id: this.hotel.id!,
        rate_param: this.rateParam,
        los: this.los,
        roomtype: this.roomType,
        membershiptype: this.membershipType,
        mealtype: this.mealPlan,
        type: exportChoice.type,
        from_date_range_start: today.format('YYYY-MM-DD'),
        from_date_range_end: today

          .add(exportChoice.selection || 0, 'day')
          .format('YYYY-MM-DD'),
        platform: this.platform,
        persons: this.maxPersons,
        base_membershiptype: this.showMemberRate
          ? MEMBERSHIPTYPES.MEMBER_ONLY
          : MEMBERSHIPTYPES.PUBLIC,
      };

      const url = formatUrl(
        '/apigateway/v1/app/download/integrities',
        params,
        this.ajax.host,
      );

      this.usageAnalytics.track('Data Exported', {
        eventTrigger: 'parity-table-view',
        type: exportChoice.type,
      });

      logReportInput(url, this.access.isStaffUser);

      openUrl(url);
    }
  }

  @action selectionsChanged(
    selection: SelectionsKey,
    value: SelectionsValue,
  ): void {
    this.usageAnalytics.track('Filter Applied', {
      eventTrigger: 'filter-bar',
      filterType: selection,
      filterValue: `${value}`,
    });

    this.router.transitionTo({
      queryParams: { [selection]: value },
    });
  }

  @action toggleMemberRate(): void {
    this.usageAnalytics.track('Member Rate Toggled', {
      eventTrigger: 'filter-bar',
      memberRateStatus: this.showMemberRate ? 'off' : 'on',
    });

    this.router.transitionTo({
      queryParams: { showMemberRate: !this.showMemberRate },
    });
  }

  @action triggerLiveshop(): void {
    this.usageAnalytics.track('Rates Refreshed', {
      eventTrigger: 'filter-bar',
    });

    if (this.liveupdate.isPolling) {
      return;
    }

    const params = { filterOtas: this.availableOTAs.map((ota) => ota.id!) };

    if (this.showParityAnalyticsData) {
      this.liveupdate.startLiveParityShop({
        ...params,
        parityType: LIVEUPDATE_TYPE_PARITY_INSIGHT,
      });
    } else {
      this.liveupdate.startLiveParityShop(params);
    }
  }

  @action updateSelectedMonth(selectedRange: {
    end: Dayjs;
    start: Dayjs;
  }): void {
    if (selectedRange.start) {
      this.month = selectedRange.start.format('YYYY-MM');
    }
  }
}
